import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearchParams } from 'react-router-dom';
import { MessageSquare, MessageCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useFeedbackChat } from '@/contexts/useFeedbackChat';
import { FeedbackFormContent } from './form/FeedbackFormContent';
import { formSchema, FormValues } from './form/types';
import { FeedbackFormData } from '@/types/feedback';
import { ChatInterface } from '@/features/chat/components';
import { AdminChatInterface } from '@/features/chat/components/AdminChatInterface';
import { ADMIN_USER_ID } from '@/features/chat/constants';
import { usePermissions } from '@/contexts/unified-permissions';

/**
 * Tabbed dialog that combines feedback and chat functionality
 * Users can switch between "Feedback" and "Chat" tabs within the same dialog
 */
export const TabbedFeedbackChatDialog: React.FC = () => {
  const {
    isFeedbackOpen,
    isChatOpen,
    closeFeedback,
    closeChat,
    submitFeedback,
    isSubmitting,
    currentPageSource,
    feedbackType
  } = useFeedbackChat();
  const [activeTab, setActiveTab] = useState<'feedback' | 'chat'>('feedback');
  const [screenshot, setScreenshot] = useState<File | undefined>(undefined);
  const [searchParams] = useSearchParams();

  // Determine if dialog should be open
  const isDialogOpen = isFeedbackOpen || isChatOpen;

  // Form setup
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      type: feedbackType || 'Bug',
      description: '',
      steps: '',
      urgency: 'Medium',
      contactMethod: 'Email',
      phoneNumber: '',
      additionalComments: '',
      techniqueName: '',
    },
    mode: 'onChange',
  });

  const handleClose = () => {
    // Close both feedback and chat
    closeFeedback();
    closeChat();
    // Reset to feedback tab when closing
    setActiveTab('feedback');
    // Reset form and screenshot
    form.reset();
    setScreenshot(undefined);
  };

  // Reset form when dialog closes
  useEffect(() => {
    if (!isDialogOpen) {
      form.reset();
      setScreenshot(undefined);
    }
  }, [isDialogOpen, form]);

  // Switch to chat tab when chat is opened
  useEffect(() => {
    if (isChatOpen && !isFeedbackOpen) {
      setActiveTab('chat');
    } else if (isFeedbackOpen && !isChatOpen) {
      setActiveTab('feedback');
    }
  }, [isChatOpen, isFeedbackOpen]);

  // Apply feedback type from context or URL params
  useEffect(() => {
    if (isFeedbackOpen) {
      if (feedbackType) {
        form.setValue('type', feedbackType);
      } else {
        const urlFeedbackType = searchParams.get('type');
        if (
          urlFeedbackType &&
          ['Bug', 'Issue/Concern', 'Feature Request', 'Technique Request'].includes(urlFeedbackType)
        ) {
          form.setValue('type', urlFeedbackType as FormValues['type']);
        }
      }
    }
  }, [isFeedbackOpen, feedbackType, searchParams, form]);

  const onSubmit = async (values: FormValues) => {
    try {
      if (values.type === 'Technique Request' && !values.techniqueName) {
        toast.error('Please provide a name for the technique');
        return;
      }

      const feedbackData: FeedbackFormData = {
        type: values.type,
        description: values.description,
        urgency: values.type === 'Technique Request' ? 'Medium' : values.urgency,
        contactMethod: values.type === 'Technique Request' ? 'Email' : values.contactMethod,
        steps: values.type === 'Technique Request' ? undefined : values.steps,
        phoneNumber: values.type === 'Technique Request' ? undefined : values.phoneNumber,
        additionalComments: values.type === 'Technique Request' ? undefined : values.additionalComments,
        pageSource: currentPageSource,
        ...(screenshot && values.type !== 'Technique Request' && { screenshot }),
        ...(values.type === 'Technique Request' && values.techniqueName && { techniqueName: values.techniqueName }),
      };

      await submitFeedback(feedbackData);
      form.reset();
      setScreenshot(undefined);
    } catch (error: unknown) {
      if (error instanceof Error) {
        console.error('Failed to submit feedback:', error.message);
      }
      toast.error('Failed to submit feedback. Please try again.');
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0 pb-4">
          <DialogTitle className="flex items-center gap-2">
            {activeTab === 'feedback' ? (
              <>
                <MessageSquare className="h-5 w-5" />
                {form.watch('type') === 'Technique Request' ? 'Request a Technique' : 'Share Your Feedback'}
              </>
            ) : (
              <>
                <MessageCircle className="h-5 w-5" />
                Chat with Support
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {activeTab === 'feedback'
              ? (form.watch('type') === 'Technique Request'
                  ? 'Request a new technique to be added to the library'
                  : 'Help us improve by letting us know what you think')
              : 'Send a message directly to our team'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'feedback' | 'chat')} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2 flex-shrink-0 mb-4">
            <TabsTrigger value="feedback" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              Feedback
            </TabsTrigger>
            <TabsTrigger value="chat" className="flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              Chat
            </TabsTrigger>
          </TabsList>

          <TabsContent value="feedback" className="flex-1 overflow-y-auto overscroll-contain data-[state=active]:flex data-[state=active]:flex-col">
            <FeedbackFormContent
              form={form}
              screenshot={screenshot}
              setScreenshot={setScreenshot}
              onSubmit={onSubmit}
              onCancel={handleClose}
              isSubmitting={isSubmitting}
            />
          </TabsContent>

          <TabsContent value="chat" className="flex-1 overflow-hidden data-[state=active]:flex data-[state=active]:flex-col">
            <ChatContent />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Chat content for the dialog
 */
const ChatContent: React.FC = () => {
  const { isAdmin } = usePermissions();

  // Admins get a special interface to manage multiple conversations
  // Regular users chat directly with admin support
  if (isAdmin) {
    return (
      <AdminChatInterface height="h-full" />
    );
  }

  return (
    <ChatInterface
      otherUserId={ADMIN_USER_ID}
      otherUserName="Support"
      height="h-full"
    />
  );
};
